import { useCallback, useState } from 'react';
import { Form, FormProps, Collapse, Flex, CollapseProps } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as NextArrowIcon } from '@Icons/additional/arrow-left.svg';
import { ReactComponent as ArrowDownIcon } from '@Icons/additional/chevron-down-gradient.svg';
import { ReactComponent as ArrowRightIcon } from '@Icons/additional/chevron-right.svg';
import { ReactComponent as ShoppingBagIcon } from '@Icons/additional/shopping-bag-gradient.svg';

import InfiniteScroll from 'react-infinite-scroll-component';

import { useAppDispatch, useAppSelector } from '@Hooks/index';
import { useHeaderCaption } from '@Hooks/useHeaderCaption';

import { MButton, MInput, MSkeleton } from '@Components/Base';

import { PurchaseFormResponse, PurchaseItemResponse } from '@Types/api/form';
import { RequestListSearchResponse } from '@Types/api/requestList';
import { FooterActions } from '@Enum/index';
import MRFeatureRoutePath from '@Features/router/mr/MRFeatureRoutePath.enum';

import { createdTender } from '@Features/MaterialRequest/requests/PurchaseRequest/store/selectors/purchaseRequestSelector';
import ViewDetailesModal from '@Features/MaterialRequest/requests/components/ViewDetailesModal/ViewDetailesModal';

import CollapseItems from '../components/CollapseItems/CollapseItems';
import RequisitionInformation from './components/RequisitionInformation/RequisitionInformation';
import FooterButtonSection from './components/FooterButtonSection/FooterButtonSection';
import { OverviewMaterials } from '../components/OverviewMaterials';

import { purchaseRequestActions } from '../../../store/purchaseRequestSlice';

import styles from './create-tender-mobile.module.css';

const expandIcon = ({ isActive }: { isActive?: boolean }): JSX.Element => (
  <ArrowDownIcon
    className={classNames(styles.expandIcon, { [styles.active]: isActive })}
  />
);

interface CreateTenderMobileProps {
  className?: string;
  layout?: FormProps['layout'];
  listRequests: RequestListSearchResponse<PurchaseFormResponse>;
  requestsDataList: PurchaseFormResponse[];
  purchaseItems: Record<number, PurchaseItemResponse[]>;
  overviewItems: PurchaseItemResponse[];
  selectedOverviewItems: PurchaseItemResponse[];
  onGetPurchaseItems: (requestId: number) => void;
  handleCreateTender: () => void;
  loadMore: () => void;
  handleReviewTender: () => void;
}

const CreateTenderMobile: React.FC<CreateTenderMobileProps> = ({
  className,
  layout = 'vertical',
  listRequests,
  requestsDataList,
  purchaseItems,
  overviewItems,
  selectedOverviewItems,
  onGetPurchaseItems,
  handleCreateTender,
  loadMore,
  handleReviewTender,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [fetchedSectionsKeys, setFetchedSectionsKeys] = useState<string[]>([]);
  const tenderId = useAppSelector(createdTender);
  const [isViewDetailesOpen, setIsViewDetailesOpen] = useState(false);
  const [purchaseDetails, setPurchaseDetails] =
    useState<PurchaseFormResponse>();
  const [isOverviewOpen, setIsOverviewOpen] = useState(false);

  const headerCaption = useHeaderCaption(false);
  const handleBack = (): void => {
    dispatch(purchaseRequestActions.clearBasket());
    navigate(
      `/mr/${MRFeatureRoutePath.PROCUREMENT}/${MRFeatureRoutePath.PROCUREMENT_PURCHASE_REQUEST}`
    );
  };

  const handleAddMaterial = (purchaseItem: PurchaseItemResponse): void => {
    dispatch(purchaseRequestActions.addOverviewItem(purchaseItem));
    dispatch(purchaseRequestActions.setSelectedOverviewItem(purchaseItem));
  };

  const handleRemoveMaterial = (purchaseItem: PurchaseItemResponse): void => {
    dispatch(purchaseRequestActions.removeOverviewItem(purchaseItem));
  };

  const handleRemoveAllMaterial = (): void => {
    dispatch(purchaseRequestActions.removeAllOverviewItem());
  };

  const handleSelectOverviewItem = (
    purchaseItem: PurchaseItemResponse
  ): void => {
    dispatch(purchaseRequestActions.setSelectedOverviewItem(purchaseItem));
  };

  const handleUnselectOverviewItem = (
    purchaseItem: PurchaseItemResponse
  ): void => {
    dispatch(purchaseRequestActions.setUnselectedOverviewItem(purchaseItem));
  };

  const handleSelectAllOverviewItems = (): void => {
    dispatch(purchaseRequestActions.selectAllOverviewItems());
  };

  const handleUnselectAllOverviewItems = (): void => {
    dispatch(purchaseRequestActions.unselectAllOverviewItems());
  };

  const handleViewDetailes = (purchaseItem: PurchaseFormResponse): void => {
    setPurchaseDetails(purchaseItem);
    setIsViewDetailesOpen(!isViewDetailesOpen);
  };

  const handleCloseViewDetailes = (): void => {
    setIsViewDetailesOpen(!isViewDetailesOpen);
  };

  const handleCollapse: CollapseProps['onChange'] = (key): void => {
    if (key && key.length > 0) {
      const newSectionKey = key.find((k) => !fetchedSectionsKeys.includes(k));

      if (newSectionKey) {
        const sectionId = Number(newSectionKey);
        onGetPurchaseItems(sectionId);
      }

      setFetchedSectionsKeys(key);
    }
  };

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleCreateTender();
      }

      if (action === FooterActions.REVIEW_AND_CONFIRM) {
        handleReviewTender();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        dispatch(purchaseRequestActions.setCreatedTenderId(0));
        navigate(
          `/mr/${MRFeatureRoutePath.PROCUREMENT}/${MRFeatureRoutePath.PROCUREMENT_PURCHASE_REQUEST}`
        );
      }
    },
    [handleCreateTender, handleReviewTender]
  );

  return (
    <div>
      <div className={styles.formWrapper}>
        <div className={styles.formBackSection}>
          <NextArrowIcon onClick={handleBack} />
          <span>{headerCaption}</span>
        </div>
        <RequisitionInformation
          requisitionNo={String(tenderId)}
          requisitionDate={dayjs().format('DD/MM/YYYY')}
          requisitionStatus="New"
        />
        <Form
          className={classNames(styles.form, className)}
          layout={layout}
          requiredMark={false}
        >
          <div className={styles.formContent}>
            <Flex vertical gap={16}>
              <div>
                <span className={styles.formHeaderTitle}>Create tender</span>
                <span className={styles.formHeaderText}>
                  Select the materials that you need to create this tender
                </span>
              </div>

              <MInput searchInput placeholder="Search..." />
              <div id="scrollableDiv" className={styles.scrollableContainer}>
                <InfiniteScroll
                  next={loadMore}
                  hasMore={
                    requestsDataList.length < listRequests?.totalElements
                  }
                  loader={<MSkeleton paragraph={{ rows: 1 }} active />}
                  dataLength={requestsDataList.length}
                  scrollableTarget="scrollableDiv"
                >
                  <Collapse
                    className={styles.collapseSection}
                    ghost
                    expandIcon={expandIcon}
                    onChange={handleCollapse}
                    items={CollapseItems({
                      requestsDataList,
                      purchaseItems,
                      overviewItems,
                      handleAddMaterial,
                      handleRemoveMaterial,
                      handleViewDetailes,
                    })}
                  />
                </InfiniteScroll>
              </div>
            </Flex>
          </div>
          <div className={classNames(styles.formFooterContent)}>
            <MButton
              className={classNames(styles.overviewButton)}
              onClick={() => setIsOverviewOpen(true)}
            >
              <ShoppingBagIcon className={classNames(styles.cartIcon)} />
              <span> Overview ({overviewItems.length})</span>
              <ArrowRightIcon />
            </MButton>

            <FooterButtonSection
              disabledSaveForm={Boolean(selectedOverviewItems.length)}
              disabledReview={!selectedOverviewItems.length && tenderId === 0}
              onFooterAction={handleFooterAction}
            />
          </div>
        </Form>
      </div>
      <ViewDetailesModal
        purchaseDetails={purchaseDetails}
        isOpen={isViewDetailesOpen}
        onClose={handleCloseViewDetailes}
      />
      <OverviewMaterials
        handleAddMaterial={handleAddMaterial}
        handleRemoveMaterial={handleRemoveMaterial}
        handleRemoveAllMaterial={handleRemoveAllMaterial}
        handleUnselectOverviewItem={handleUnselectOverviewItem}
        handleSelectOverviewItem={handleSelectOverviewItem}
        handleSelectAllOverviewItems={handleSelectAllOverviewItems}
        handleUnselectAllOverviewItems={handleUnselectAllOverviewItems}
        selectedOverviewItems={selectedOverviewItems}
        overviewItems={overviewItems}
        isOpen={isOverviewOpen}
        onClose={() => setIsOverviewOpen(false)}
      />
    </div>
  );
};

export default CreateTenderMobile;
